import { useContext } from 'react';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser';
import { FeaturesEnum, PermissionTypesEnum } from '@api/ringil-api.v2';
import { Clause, ModuleRequirement, RuleKind, SecuredFlag } from './types';
import { useRegisterSecuredFlag } from './useRegisterSecuredFlag';

interface NamedGuard {
  label: string;
  satisfied: boolean;
  /** What this one condition is for, when it differs from the rule's description. */
  description?: string;
  /** Overrides the rule's byDesign for this condition (e.g. owner-only next to a lifted lock). */
  byDesign?: boolean;
}

const guardClause = <T extends 'state' | 'ownership' | 'data' | 'custom'>(type: T, g: NamedGuard) => ({
  type,
  label: g.label,
  satisfied: g.satisfied,
  ...(g.description !== undefined && { description: g.description }),
  ...(g.byDesign !== undefined && { byDesign: g.byDesign }),
});

export interface SecuredFlagOptions {
  kind?: RuleKind;
  description?: string;
  permissions?: PermissionTypesEnum[];
  features?: FeaturesEnum[];
  modules?: ModuleRequirement[];
  state?: NamedGuard[];
  ownership?: NamedGuard;
  data?: NamedGuard[];
  custom?: NamedGuard[];
  /** true when this rule blocking is intended in that situation, false when something can change it. */
  byDesign?: boolean;
}

/**
 * Declares a named, reusable security rule from permissions / features /
 * state / ownership / data guards. Derives both the boolean and the clause
 * metadata so the permission set is declared exactly once.
 */
export function useSecuredFlag(id: string, opts: SecuredFlagOptions): SecuredFlag {
  const { hasPermission, hasFeature, hasModule } = useContext(AuthenticatedUserContext);

  const permOk = opts.permissions ? hasPermission(opts.permissions) : true;
  const featOk = opts.features ? hasFeature(opts.features) : true;
  const moduleOk = opts.modules
    ? opts.modules.some(m => hasModule(m.module, m.inherited as boolean))
    : true;
  const stateOk = (opts.state ?? []).every(s => s.satisfied);
  const ownerOk = opts.ownership ? opts.ownership.satisfied : true;
  const dataOk = (opts.data ?? []).every(d => d.satisfied);
  const customOk = (opts.custom ?? []).every(c => c.satisfied);

  return useRegisterSecuredFlag(
    id,
    () => permOk && featOk && moduleOk && stateOk && ownerOk && dataOk && customOk,
    () => {
      const clauses: Clause[] = [];
      for (const s of opts.state ?? []) clauses.push(guardClause('state', s));
      if (opts.ownership) clauses.push(guardClause('ownership', opts.ownership));
      if (opts.permissions) clauses.push({ type: 'permission', anyOf: opts.permissions, satisfied: permOk });
      if (opts.features) clauses.push({ type: 'feature', anyOf: opts.features, satisfied: featOk });
      if (opts.modules) clauses.push({ type: 'module', anyOf: opts.modules, satisfied: moduleOk });
      for (const d of opts.data ?? []) clauses.push(guardClause('data', d));
      for (const c of opts.custom ?? []) clauses.push(guardClause('custom', c));
      return {
        kind: opts.kind ?? 'visibility',
        description: opts.description,
        byDesign: opts.byDesign,
        clauses,
      };
    },
  );
}